// --------------------------------------------------------------
// Created On: 2022-05-02
// Author: Zachary Thomas
//
// Last Modified: 2026-04-24
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useEffect } from "react";
import Spinner from "../../components/Spinner/Spinner";
import { useNavigate } from "react-router-dom";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import { setIdToken } from "src/redux/features/idTokenSlice";
import { setAccessToken } from "src/redux/features/accessTokenSlice";
import { setRefreshToken } from "src/redux/features/refreshTokenSlice";
import { setSessionToken } from "src/redux/features/sessionTokenSlice";
import { setCurrentUser } from "src/redux/features/currentUserSlice";
import { setMobileNewPasswordRequired } from "src/redux/features/mobileNewPasswordRequiredSlice";
import { setMobileMfaChallengeRequired } from "src/redux/features/mobileMfaChallengeRequiredSlice";
import { setMobileMfaSetupRequired } from "src/redux/features/mobileMfaSetupRequiredSlice";

// RPM² mobile app will redirect from MobileAppLandingPage to this page when it finished writing to local storage.
// Once this page completes reading from local storage, it will redirect to LoginPage and automate authentication.
export default function MobileAppLoadingPage(): Component {
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  // Attempt to load local storage on initial page load.
  useEffect(() => {
    try {
      let isLegacyMobileApp = false;
      let idToken = getResourceFromLocalStorage("reduxIdToken");
      const legacyIdToken = getResourceFromLocalStorage("reduxAuthorizerToken");
      localStorage.setItem("reduxAuthorizerToken", "");

      // If we have a legacy ID token, then it means we are still using the old mobile app and should use it.
      if (legacyIdToken !== null) {
        idToken = legacyIdToken;
      }

      const accessToken = getResourceFromLocalStorage("reduxAccessToken");
      const refreshToken = getResourceFromLocalStorage("reduxRefreshToken");
      const sessionToken = getResourceFromLocalStorage("reduxSessionToken");
      const currentUser = getResourceFromLocalStorage("reduxCurrentUser");

      const newPasswordRequired = getResourceFromLocalStorage("reduxNewPasswordRequired");
      const mfaChallengeRequired = getResourceFromLocalStorage("reduxMfaChallengeRequired");
      const mfaSetupRequired = getResourceFromLocalStorage("reduxMfaSetupRequired");

      if (currentUser === null) {
        dispatch(
          setCurrentUser({
            userId: 0,
            userName: "",
            emailAddress: "",
            originalCompanyId: 0,
            originalCompanyName: "",
            companyId: 0,
            companyName: "",
            userCompanyPath: [],
            isCompanyHidden: false,
            permissions: [],
            originalPermissions: [],
            isPackager: false,
            mfaEnabled: false,
          })
        );
      } else {
        const parsedCurrentUser: CurrentUser = JSON.parse(currentUser) as CurrentUser;
        if ("agreedToEula" in parsedCurrentUser || mfaChallengeRequired === null) {
          isLegacyMobileApp = true;
        }
        dispatch(
          setCurrentUser({
            userId: parsedCurrentUser.userId ?? 0,
            userName: parsedCurrentUser.userName ?? "",
            emailAddress: parsedCurrentUser.emailAddress ?? "",
            originalCompanyId: parsedCurrentUser.originalCompanyId ?? 0,
            originalCompanyName: parsedCurrentUser.originalCompanyName ?? "",
            originalPermissions: parsedCurrentUser.originalPermissions ?? [],
            companyId: parsedCurrentUser.companyId ?? 0,
            companyName: parsedCurrentUser.companyName ?? "",
            isCompanyHidden: parsedCurrentUser.isCompanyHidden ?? false,
            userCompanyPath: parsedCurrentUser.userCompanyPath ?? [],
            permissions: parsedCurrentUser.permissions ?? [],
            isPackager: parsedCurrentUser.isPackager ?? false,
            mfaEnabled: parsedCurrentUser.mfaEnabled ?? false,
          })
        );
      }

      dispatch(setIdToken(idToken));
      dispatch(setAccessToken(accessToken));
      dispatch(setRefreshToken(refreshToken));
      dispatch(setSessionToken(sessionToken));

      if (newPasswordRequired === "true") {
        dispatch(setMobileNewPasswordRequired(true));
      } else {
        dispatch(setMobileNewPasswordRequired(false));
      }

      // Don't update MFA redux state or navigate to the login page if on a legacy version of the application.
      if (!isLegacyMobileApp) {
        if (mfaChallengeRequired === "true") {
          dispatch(setMobileMfaChallengeRequired(true));
        } else {
          dispatch(setMobileMfaChallengeRequired(false));
        }

        if (mfaSetupRequired === "true") {
          dispatch(setMobileMfaSetupRequired(true));
        } else {
          dispatch(setMobileMfaSetupRequired(false));
        }

        navigate("/login/mobile");
      }
    } catch (e) {
      console.error(e);
      console.error("Redux state was unable to load from mobile app loading page.");
    }
  }, []);

  // Gets a resource from local storage and logs information about what was returned.
  function getResourceFromLocalStorage(resourceName: string): string | null {
    let localStorageResource = localStorage.getItem(resourceName);
    if (localStorageResource === null || localStorageResource === "") {
      console.log(`${resourceName} not found in local storage. ❌`);
      localStorageResource = null;
    } else {
      console.log(`${resourceName} loaded from local storage. ✅`);
    }
    return localStorageResource;
  }

  return <Spinner loading={true} />;
}
